import { getItemScheduleLabel, isScheduleMetaKey, isScheduledDataItem } from '@utils/repeatableDatasetSchedule';

const SCHEDULE_LABEL_DUPE_KEYS = new Set(['title', 'name', 'label', 'task']);

const DATETIME_SCHEMA_TYPES = new Set([
  'datetime',
  'dateTime',
  'date',
  'time',
  'timestamp',
]);

const DATETIME_KEY_RE =
  /(^|_)(date|time|datetime|timestamp|starts?|ends?|begins?)(_|$)|^(when|from|to)$|scheduled_?at|start_?at|end_?at|start_?time|end_?time|start_?date|end_?date/i;

const TIME_ONLY_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?$/i;

export type SchemaFieldMeta = {
  name: string;
  type?: string;
};

export type InferDisplayFieldsOptions = {
  /** Programme / schedule lists: surface date/time fields before other values. */
  prioritizeDateTime?: boolean;
};

function schemaTypeOf(entry: Record<string, unknown>): string | undefined {
  const nested = entry.type ?? entry.ref ?? (entry as { validation?: { type?: unknown } }).validation?.type;
  if (nested && typeof nested === 'object') {
    const inner = nested as Record<string, unknown>;
    const nestedType = inner.type ?? inner.ref;
    return nestedType ? String(nestedType) : undefined;
  }
  return nested != null ? String(nested) : undefined;
}

/** Resolve schema field names plus declared types (array or record). */
export function getSchemaFieldMeta(schema: unknown): SchemaFieldMeta[] {
  if (!schema) return [];
  if (Array.isArray(schema)) {
    const fields: SchemaFieldMeta[] = [];
    for (const entry of schema) {
      if (!entry || typeof entry !== 'object') continue;
      const row = entry as Record<string, unknown>;
      const explicit = row.title || row.key || row.name;
      if (explicit) {
        fields.push({ name: String(explicit), type: schemaTypeOf(row) });
        continue;
      }
      const pairs = Object.entries(row);
      if (pairs.length === 1) {
        const [name, spec] = pairs[0];
        const specType =
          spec && typeof spec === 'object' ? schemaTypeOf(spec as Record<string, unknown>) : undefined;
        fields.push({ name: String(name), type: specType });
      }
    }
    return fields;
  }
  if (typeof schema === 'object') {
    return Object.entries(schema as Record<string, unknown>).map(([name, spec]) => ({
      name,
      type: spec && typeof spec === 'object' ? schemaTypeOf(spec as Record<string, unknown>) : undefined,
    }));
  }
  return [];
}

/** Resolve displayable field names from backend schema (array or record). */
export function getSchemaFieldNames(schema: unknown): string[] {
  return getSchemaFieldMeta(schema).map((field) => field.name);
}

export function isDateTimeSchemaType(type?: string): boolean {
  if (!type) return false;
  return DATETIME_SCHEMA_TYPES.has(type) || DATETIME_SCHEMA_TYPES.has(type.toLowerCase());
}

export function isDateTimeFieldKey(fieldKey: string): boolean {
  const normalized = fieldKey.toLowerCase().replace(/\s+/g, '_');
  return DATETIME_KEY_RE.test(normalized);
}

export function parseDisplayDateTime(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const timeOnly = trimmed.match(TIME_ONLY_RE);
  if (timeOnly) {
    let hours = Number(timeOnly[1]);
    const minutes = Number(timeOnly[2]);
    const seconds = timeOnly[3] ? Number(timeOnly[3]) : 0;
    const meridiem = timeOnly[4]?.toLowerCase();
    if (meridiem === 'pm' && hours < 12) hours += 12;
    if (meridiem === 'am' && hours === 12) hours = 0;
    const parsed = new Date();
    parsed.setHours(hours, minutes, seconds, 0);
    return parsed;
  }

  const fromString = new Date(trimmed);
  if (!Number.isNaN(fromString.getTime())) return fromString;
  return null;
}

function schemaTypeByName(schema: unknown): Map<string, string | undefined> {
  const map = new Map<string, string | undefined>();
  for (const field of getSchemaFieldMeta(schema)) {
    map.set(field.name, field.type);
  }
  return map;
}

export function isDateTimeDisplayField(
  fieldKey: string,
  value: unknown,
  schemaType?: string,
): boolean {
  if (isDateTimeSchemaType(schemaType)) return true;
  if (isDateTimeFieldKey(fieldKey)) return true;
  return parseDisplayDateTime(value) != null && typeof value !== 'number';
}

export function getProgrammeDateTimeMs(item: any, schema?: unknown): number | null {
  const data = item?.data;
  if (!data || typeof data !== 'object') return null;
  const types = schemaTypeByName(schema);
  let earliest: number | null = null;

  for (const [key, raw] of Object.entries(data as Record<string, unknown>)) {
    if (!isDateTimeDisplayField(key, raw, types.get(key))) continue;
    const parsed = parseDisplayDateTime(raw);
    if (!parsed) continue;
    const ms = parsed.getTime();
    if (earliest == null || ms < earliest) earliest = ms;
  }

  return earliest;
}

export function sortItemsByProgrammeDateTime<T>(items: T[], schema?: unknown): T[] {
  if (!Array.isArray(items) || items.length < 2) return items;
  return [...items].sort((a, b) => {
    const aMs = getProgrammeDateTimeMs(a, schema);
    const bMs = getProgrammeDateTimeMs(b, schema);
    if (aMs == null && bMs == null) return 0;
    if (aMs == null) return 1;
    if (bMs == null) return -1;
    return aMs - bMs;
  });
}

export function formatProgrammeTimeLabel(value: unknown): string {
  const parsed = parseDisplayDateTime(value);
  if (!parsed) return typeof value === 'string' ? value.trim() : formatDisplayFieldValue(value);
  const hasExplicitTime =
    typeof value === 'string' && TIME_ONLY_RE.test(value.trim())
      ? true
      : parsed.getHours() !== 0 || parsed.getMinutes() !== 0 || parsed.getSeconds() !== 0;
  if (typeof value === 'string' && TIME_ONLY_RE.test(value.trim())) {
    return parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  return parsed.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(hasExplicitTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
}

export function isScheduleLabelDuplicateField(
  item: unknown,
  fieldKey: string,
  rawValue: unknown,
): boolean {
  if (!isScheduledDataItem(item)) return false;
  const scheduleLabel = getItemScheduleLabel(item);
  if (!scheduleLabel) return false;
  if (!SCHEDULE_LABEL_DUPE_KEYS.has(fieldKey.toLowerCase())) return false;
  return String(rawValue ?? '').trim() === scheduleLabel;
}

/** Whether a stored value is worth showing in list/card summaries. */
export function isMeaningfulDisplayValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'boolean') return value === true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return false;
  return true;
}

/** Format a field value for list/card display without mangling user text. */
export function formatDisplayFieldValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    return value
      .map((entry) => (entry == null ? '' : String(entry).trim()))
      .filter(Boolean)
      .join(', ');
  }
  return String(value);
}

/** Keys that identify a product, service, or pitch booking, most specific first. */
const RECORD_DISPLAY_NAME_KEYS = [
  'servicename',
  'pitchname',
  'pitch',
  'productname',
  'eventname',
  'bookingname',
  'facilityname',
  'facility',
  'courtname',
  'court',
  'name',
  'title',
  'label',
  'service',
  'displayname',
  'playername',
  'firstname',
  'fullname',
];

function normalizeRecordKey(key: string): string {
  return key.toLowerCase().replace(/[\s_-]+/g, '');
}

function isRawObjectId(value: string): boolean {
  return /^[0-9a-f]{24}$/i.test(value.trim());
}

function scalarDisplayName(value: unknown, depth = 0): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '[object Object]' || isRawObjectId(trimmed) || trimmed.startsWith('enc:')) return '';
    return trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (!value || typeof value !== 'object' || Array.isArray(value) || depth > 0) return '';
  const row = value as Record<string, unknown>;
  for (const key of RECORD_DISPLAY_NAME_KEYS) {
    const match = Object.keys(row).find((entry) => normalizeRecordKey(entry) === key);
    if (!match) continue;
    const nested = scalarDisplayName(row[match], depth + 1);
    if (nested) return nested;
  }
  return '';
}

/**
 * Best human label for a data-item payload.
 * Pitch bookings and services often store the name on `pitch`, `pitch_name`, or `service_name`
 * rather than `name` / `title`.
 */
export function pickRecordDisplayName(data: unknown, preferredKey?: string | null): string {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return '';
  const entries = Object.entries(data as Record<string, unknown>);
  if (preferredKey) {
    const wanted = normalizeRecordKey(preferredKey);
    const preferred = entries.find(([key]) => normalizeRecordKey(key) === wanted);
    const preferredLabel = preferred ? scalarDisplayName(preferred[1]) : '';
    if (preferredLabel) return preferredLabel;
  }
  const byKey = new Map<string, unknown>();
  for (const [key, value] of entries) {
    const normalized = normalizeRecordKey(key);
    if (!byKey.has(normalized)) byKey.set(normalized, value);
  }
  for (const key of RECORD_DISPLAY_NAME_KEYS) {
    if (!byKey.has(key)) continue;
    const label = scalarDisplayName(byKey.get(key));
    if (label) return label;
  }
  for (const [key, value] of entries) {
    const normalized = normalizeRecordKey(key);
    if (/description|summary|notes|email|phone|price|status|currency/.test(normalized)) continue;
    if (!/(name|title|pitch)$/.test(normalized)) continue;
    const label = scalarDisplayName(value);
    if (label) return label;
  }
  return '';
}

function isScalarDisplayable(
  item: unknown,
  fieldKey: string,
  value: unknown,
): boolean {
  if (isScheduleMetaKey(fieldKey)) return false;
  if (!isMeaningfulDisplayValue(value)) return false;
  // Arrays are summarized; nested objects are not.
  if (typeof value === 'object' && !Array.isArray(value)) return false;
  if (isScheduleLabelDuplicateField(item, fieldKey, value)) return false;
  return true;
}

function fieldPriority(value: unknown): number {
  if (typeof value === 'string' && value.trim()) return 3;
  if (typeof value === 'number' && Number.isFinite(value)) return 2;
  if (typeof value === 'boolean' && value === true) return 1;
  if (Array.isArray(value) && value.length > 0) return 1;
  return 0;
}

function datetimeFieldRank(fieldKey: string, value: unknown, schemaType?: string): number {
  if (!isDateTimeDisplayField(fieldKey, value, schemaType)) return 0;
  const key = fieldKey.toLowerCase();
  if (/start|begin|from/.test(key)) return 6;
  if (/end|until|to$|_to$/.test(key)) return 5;
  if (isDateTimeSchemaType(schemaType)) return 4;
  if (isDateTimeFieldKey(fieldKey)) return 3;
  return 2;
}

/** Best N display fields from a data item's schema / data. */
export function inferDisplayFields(
  item: any,
  schema: unknown,
  displayCount: number = 3,
  customOrder: string[] = [],
  options: InferDisplayFieldsOptions = {},
): string[] {
  if (!item?.data) return [];
  const data = item.data as Record<string, unknown>;
  const maxFields = Math.max(1, Math.min(displayCount, 8));
  const types = schemaTypeByName(schema);

  const isDisplayable = (fieldKey: string): boolean =>
    isScalarDisplayable(item, fieldKey, data[fieldKey]);

  const picked: string[] = [];
  const pushField = (fieldKey: string) => {
    if (picked.length >= maxFields) return;
    if (picked.includes(fieldKey)) return;
    if (!isDisplayable(fieldKey)) return;
    picked.push(fieldKey);
  };

  if (customOrder.length > 0) {
    for (const fieldKey of customOrder) {
      pushField(fieldKey);
    }
  }

  if (options.prioritizeDateTime && picked.length < maxFields) {
    const datetimeKeys = [
      ...getSchemaFieldNames(schema),
      ...Object.keys(data),
    ].filter((key, index, all) => all.indexOf(key) === index);
    datetimeKeys.sort((a, b) => {
      const rankDelta =
        datetimeFieldRank(b, data[b], types.get(b)) - datetimeFieldRank(a, data[a], types.get(a));
      if (rankDelta !== 0) return rankDelta;
      const aMs = parseDisplayDateTime(data[a])?.getTime() ?? Number.POSITIVE_INFINITY;
      const bMs = parseDisplayDateTime(data[b])?.getTime() ?? Number.POSITIVE_INFINITY;
      return aMs - bMs;
    });
    for (const fieldKey of datetimeKeys) {
      if (datetimeFieldRank(fieldKey, data[fieldKey], types.get(fieldKey)) <= 0) continue;
      pushField(fieldKey);
    }
  }

  if (picked.length < maxFields) {
    for (const field of getSchemaFieldMeta(schema)) {
      if (String(field.type || '').toLowerCase() !== 'dataset') continue;
      pushField(field.name);
    }
  }

  if (picked.length < maxFields) {
    const schemaNames = getSchemaFieldNames(schema);
    const rankedSchema = [...schemaNames].sort(
      (a, b) => fieldPriority(data[b]) - fieldPriority(data[a]),
    );
    for (const fieldKey of rankedSchema) {
      pushField(fieldKey);
    }
  }

  if (picked.length < maxFields) {
    const preferred = ['name', 'surname', 'title', 'equipment_name', 'label', 'id', 'email', 'notes', 'status', 'description'];
    for (const fieldKey of preferred) {
      pushField(fieldKey);
    }
  }

  if (picked.length < maxFields) {
    const remaining = Object.keys(data).sort(
      (a, b) => fieldPriority(data[b]) - fieldPriority(data[a]),
    );
    for (const fieldKey of remaining) {
      pushField(fieldKey);
    }
  }

  return picked;
}
